import React, { useState, useEffect } from 'react';
import { X, Search, Building2, MapPin, CreditCard, Truck, AlertCircle, CheckCircle, ArrowRight } from 'lucide-react';
import { toast } from 'react-toastify';
import apiClient from '../../api/client';
import { getDistributors } from '../../api/wholesale/distributors';

const ReassignOrderModal = ({ isOpen, onClose, order, onSuccess }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [distributors, setDistributors] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDistributor, setSelectedDistributor] = useState(null);
  const [assignOnCredit, setAssignOnCredit] = useState(false);
  const [partPaymentAmount, setPartPaymentAmount] = useState('');
  const [formData, setFormData] = useState({
    reason: '',
    reason_notes: '',
    new_delivery_address: ''
  });

  const reasons = [
    { value: 'original_customer_cancelled', label: 'Original customer cancelled' },
    { value: 'customer_unable_to_pay', label: 'Customer unable to pay' },
    { value: 'business_request', label: 'Business request' },
    { value: 'other', label: 'Other' }
  ];

  useEffect(() => {
    if (isOpen) {
      fetchDistributors();
      setStep(1);
      setSelectedDistributor(null);
      setAssignOnCredit(false);
      setPartPaymentAmount('');
      setFormData({ reason: '', reason_notes: '', new_delivery_address: '' });
    }
  }, [isOpen]);

  const fetchDistributors = async () => {
    try {
      const response = await getDistributors();
      const data = response?.data || response || [];
      // Filter out the current distributor
      const filtered = data.filter(d => d.id !== order?.distributor_id);
      setDistributors(filtered);
    } catch (err) {
      console.error('Error fetching distributors:', err);
      toast.error('Failed to load distributors');
    }
  };

  const filteredDistributors = distributors.filter(d => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      (d.business_name || '').toLowerCase().includes(query) ||
      (d.distributor_number || '').toLowerCase().includes(query) ||
      (d.contact_person_name || '').toLowerCase().includes(query)
    );
  });

  const handleSelectDistributor = (distributor) => {
    setSelectedDistributor(distributor);
    const availableCredit = parseFloat(distributor.credit_limit || 0) - parseFloat(distributor.current_credit_balance || 0);
    const needsCredit = availableCredit < parseFloat(order?.total_amount || 0);
    // Auto-enable assign on credit if distributor doesn't have sufficient credit
    setAssignOnCredit(needsCredit);
    setFormData(prev => ({
      ...prev,
      new_delivery_address: distributor.delivery_address || ''
    }));
    setStep(2);
  };

  const handleSubmit = async () => {
    if (!selectedDistributor || !formData.reason) {
      toast.error('Please select a distributor and provide a reason');
      return;
    }

    setLoading(true);
    try {
      await apiClient.post(`/wholesale/orders/${order.id}/reassign`, {
        new_distributor_id: selectedDistributor.id,
        reason: formData.reason,
        reason_notes: formData.reason_notes,
        new_delivery_address: formData.new_delivery_address || null,
        assign_on_credit: assignOnCredit === true,
        payment_verified: assignOnCredit === 'payment',
        part_payment_amount: assignOnCredit === 'partPayment' ? parseFloat(partPaymentAmount || 0) : null
      });

      toast.success('Order reassigned successfully');
      onSuccess?.();
      onClose();
    } catch (err) {
      console.error('Error reassigning order:', err);
      toast.error(err.response?.data?.error || 'Failed to reassign order');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const totalAmount = parseFloat(order?.total_amount || 0);
  const newAvailableCredit = selectedDistributor
    ? parseFloat(selectedDistributor.credit_limit || 0) - parseFloat(selectedDistributor.current_credit_balance || 0)
    : 0;
  const hasSufficientCredit = newAvailableCredit >= totalAmount;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Reassign Order</h2>
            <p className="text-sm text-gray-500 mt-1">
              Order {order?.order_number} • ₦{totalAmount.toLocaleString()}
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Progress Steps */}
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center justify-center gap-4">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center gap-2">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  step >= s ? 'bg-emerald-500 text-white' : 'bg-gray-200 text-gray-500'
                }`}>
                  {step > s ? <CheckCircle className="w-5 h-5" /> : s}
                </div>
                <span className={`text-sm ${step >= s ? 'text-gray-900' : 'text-gray-500'}`}>
                  {s === 1 ? 'Select Distributor' : s === 2 ? 'Provide Reason' : 'Confirm'}
                </span>
                {s < 3 && <ArrowRight className="w-4 h-4 text-gray-400" />}
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Step 1: Select Distributor */}
          {step === 1 && (
            <div className="space-y-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search distributors..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              <div className="space-y-3 max-h-[400px] overflow-y-auto">
                {filteredDistributors.map((dist) => {
                  const availableCredit = parseFloat(dist.credit_limit || 0) - parseFloat(dist.current_credit_balance || 0);
                  const canAfford = availableCredit >= totalAmount;

                  return (
                    <div
                      key={dist.id}
                      onClick={() => handleSelectDistributor(dist)}
                      className="p-4 border border-gray-200 rounded-lg cursor-pointer transition-colors hover:border-gray-400 hover:bg-gray-100"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <div className="p-2 bg-gray-100 rounded-lg">
                            <Building2 className="w-5 h-5 text-gray-600" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{dist.business_name}</p>
                            <p className="text-sm text-gray-500">{dist.distributor_number}</p>
                            {dist.contact_person_name && (
                              <p className="text-sm text-gray-500">{dist.contact_person_name}</p>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="flex items-center gap-1 text-sm">
                            <CreditCard className="w-4 h-4" />
                            <span className={canAfford ? 'text-emerald-600' : 'text-red-600'}>
                              ₦{availableCredit.toLocaleString()}
                            </span>
                          </div>
                          {!canAfford && (
                            <p className="text-xs text-red-600 mt-1">Insufficient credit</p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}

                {filteredDistributors.length === 0 && (
                  <p className="text-center text-gray-500 py-8">No distributors found</p>
                )}
              </div>
            </div>
          )}

          {/* Step 2: Provide Reason */}
          {step === 2 && selectedDistributor && (
            <div className="space-y-6">
              {/* Selected Distributor Summary */}
              <div className="p-4 border rounded-lg bg-white border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-gray-100">
                    <Building2 className="w-5 h-5 text-gray-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{selectedDistributor.business_name}</p>
                    <p className="text-sm text-gray-500">
                      Available Credit: ₦{newAvailableCredit.toLocaleString()}
                      {!hasSufficientCredit && <span className="text-red-600 ml-1">(Insufficient)</span>}
                    </p>
                  </div>
                  <button
                    onClick={() => setStep(1)}
                    className="ml-auto text-sm hover:underline text-blue-600"
                  >
                    Change
                  </button>
                </div>
              </div>

              {/* Payment Options when insufficient credit */}
              {!hasSufficientCredit && (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                    <div className="flex-1">
                      <p className="font-medium text-amber-800">Insufficient Credit</p>
                      <p className="text-sm text-amber-700 mt-1">
                        This distributor does not have sufficient credit (Available: ₦{newAvailableCredit.toLocaleString()}, Required: ₦{totalAmount.toLocaleString()}).
                        Choose how to proceed:
                      </p>
                      
                      <div className="mt-4 space-y-3">
                        {/* Option 1: Assign on Credit */}
                        <label className="flex items-start gap-3 p-3 bg-white border border-gray-200 rounded-lg cursor-pointer hover:border-amber-300">
                          <input
                            type="radio"
                            name="paymentOption"
                            value="credit"
                            checked={assignOnCredit === true}
                            onChange={() => setAssignOnCredit(true)}
                            className="mt-0.5 w-4 h-4 text-amber-600 focus:ring-amber-500"
                          />
                          <div>
                            <p className="font-medium text-gray-900">Assign on Credit</p>
                            <p className="text-sm text-gray-500">
                              Increase the customer's credit balance by ₦{totalAmount.toLocaleString()}
                            </p>
                          </div>
                        </label>

                        {/* Option 2: Payment Received/Verified */}
                        <label className="flex items-start gap-3 p-3 bg-white border border-gray-200 rounded-lg cursor-pointer hover:border-emerald-300">
                          <input
                            type="radio"
                            name="paymentOption"
                            value="payment"
                            checked={assignOnCredit === 'payment'}
                            onChange={() => setAssignOnCredit('payment')}
                            className="mt-0.5 w-4 h-4 text-emerald-600 focus:ring-emerald-500"
                          />
                          <div>
                            <p className="font-medium text-gray-900">Full Payment Received</p>
                            <p className="text-sm text-gray-500">
                              Customer has made full payment for this order (no credit impact)
                            </p>
                          </div>
                        </label>

                        {/* Option 3: Part Payment */}
                        <div className={`p-3 bg-white border rounded-lg ${assignOnCredit === 'partPayment' ? 'border-blue-300' : 'border-gray-200'}`}>
                          <label className="flex items-start gap-3 cursor-pointer">
                            <input
                              type="radio"
                              name="paymentOption"
                              value="partPayment"
                              checked={assignOnCredit === 'partPayment'}
                              onChange={() => setAssignOnCredit('partPayment')}
                              className="mt-0.5 w-4 h-4 text-blue-600 focus:ring-blue-500"
                            />
                            <div className="flex-1">
                              <p className="font-medium text-gray-900">Part Payment</p>
                              <p className="text-sm text-gray-500">
                                Enter amount paid, the remaining balance will be added as credit
                              </p>
                            </div>
                          </label>
                          
                          {assignOnCredit === 'partPayment' && (
                            <div className="mt-3 ml-7">
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Amount Paid (₦)
                              </label>
                              <input
                                type="number"
                                value={partPaymentAmount}
                                onChange={(e) => setPartPaymentAmount(e.target.value)}
                                placeholder="0.00"
                                min="0"
                                max={totalAmount}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              />
                              {partPaymentAmount && parseFloat(partPaymentAmount) > 0 && (
                                <p className="text-sm text-gray-600 mt-2">
                                  Remaining as credit: <span className="font-medium text-amber-600">
                                    ₦{(totalAmount - parseFloat(partPaymentAmount || 0)).toLocaleString()}
                                  </span>
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Reason Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reassignment Reason *
                </label>
                <select
                  value={formData.reason}
                  onChange={(e) => setFormData(prev => ({ ...prev, reason: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500"
                  required
                >
                  <option value="">Select a reason...</option>
                  {reasons.map(r => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Additional Notes
                </label>
                <textarea
                  value={formData.reason_notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, reason_notes: e.target.value }))}
                  placeholder="Provide any additional details..."
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              {/* Delivery Address */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    Delivery Address
                  </div>
                </label>
                <textarea
                  value={formData.new_delivery_address}
                  onChange={(e) => setFormData(prev => ({ ...prev, new_delivery_address: e.target.value }))}
                  placeholder="Enter delivery address..."
                  rows={2}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              <button
                onClick={() => setStep(3)}
                disabled={!formData.reason || (!hasSufficientCredit && !assignOnCredit) || (assignOnCredit === 'partPayment' && (!partPaymentAmount || parseFloat(partPaymentAmount) <= 0))}
                className="w-full py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Continue to Confirmation
              </button>
            </div>
          )}

          {/* Step 3: Confirm */}
          {step === 3 && selectedDistributor && (
            <div className="space-y-6">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-800">Please Review Carefully</p>
                    <p className="text-sm text-amber-700 mt-1">
                      This action will transfer the order to a new customer and cannot be easily undone.
                    </p>
                  </div>
                </div>
              </div>

              {/* Summary */}
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">From</p>
                    <p className="font-medium text-gray-900">
                      {order?.distributor_business_name || order?.business_name}
                    </p>
                  </div>
                  <div className="p-4 bg-gray-200 border border-gray-300 rounded-lg">
                    <p className="text-xs text-gray-600 uppercase tracking-wide mb-1">To</p>
                    <p className="font-medium text-gray-900">{selectedDistributor.business_name}</p>
                  </div>
                </div>

                <div className="p-4 border border-gray-200 rounded-lg space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Order Total</span>
                    <span className="font-medium">₦{totalAmount.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Reason</span>
                    <span className="font-medium">{reasons.find(r => r.value === formData.reason)?.label}</span>
                  </div>
                  {!hasSufficientCredit && (
                    <>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Payment Method</span>
                        <span className={`font-medium ${
                          assignOnCredit === 'payment' ? 'text-emerald-600' : 
                          assignOnCredit === 'partPayment' ? 'text-blue-600' : 'text-amber-600'
                        }`}>
                          {assignOnCredit === 'payment' ? 'Full Payment Verified' : 
                           assignOnCredit === 'partPayment' ? 'Part Payment' : 'Assign on Credit'}
                        </span>
                      </div>
                      {assignOnCredit === 'partPayment' && partPaymentAmount && (
                        <>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-500">Amount Paid</span>
                            <span className="font-medium text-emerald-600">
                              ₦{parseFloat(partPaymentAmount).toLocaleString()}
                            </span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-500">Added as Credit</span>
                            <span className="font-medium text-amber-600">
                              ₦{(totalAmount - parseFloat(partPaymentAmount)).toLocaleString()}
                            </span>
                          </div>
                        </>
                      )}
                    </>
                  )}
                  {formData.new_delivery_address && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Delivery Address</span>
                      <span className="font-medium text-right max-w-[200px] truncate">
                        {formData.new_delivery_address}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setStep(2)}
                  className="flex-1 py-3 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Back
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="flex-1 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Truck className="w-5 h-5" />
                      Confirm Reassignment
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReassignOrderModal;
